import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as Combobox from '@givebutter/primitives/combobox';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchFunds } from 'api/funds';
import { SoftCreditFormValues } from 'assets/js/pages/transactions/soft-credits/types';
import { AddressFormValues } from 'components/address-form/types';
import { CustomFieldFormValue } from 'components/custom-fields/helpers';
import * as Form from 'components/form';
import * as Select from 'components/select';
import { ContactType } from 'constants/contacts';
import { FUNDS_PARAMS } from 'constants/params';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentPossibleTransaction } from 'customHooks/useCurrentTransactionOrById';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteCampaignMembers } from 'customHooks/useInfiniteCampaignMembers';
import { useInfiniteCampaignTeams } from 'customHooks/useInfiniteCampaignTeams';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact, ContactEmail, ContactPhone, TransactionMethod } from 'types/domain';

interface TransactionsFormValues extends AddressFormValues {
    amount: string;
    companyName: string;
    firstName: string;
    lastName: string;
    checkNumber: string;
    offlinePaymentReceived: boolean;
    paymentMethod: TransactionMethod;
    donationDate: string;
    depositDate: string;
    message: string;
    internalNote: string;
    hideName: boolean;
    hideAmount: boolean;
    sendReceipt: boolean;
    addresses: (AddressFormValues & { id?: string; isPrimary: boolean })[];
    emails: ContactEmail[];
    phones: ContactPhone[];
    contactType: ContactType;
    selectedCampaign: string[];
    selectedContact?: Select.SelectItemWithLabel<Contact>[];
    selectedTeamMember: string[];
    selectedTeam: string[];
    selectedFund: string[];
    customFields: {
        [field: string]: CustomFieldFormValue;
    };
    dedicationType: 'in_honor_of' | 'in_memory_of';
    dedicationName: string;
    dedicationRecipientName: string;
    dedicationRecipientEmail: string;
    dedicationShouldNotifyRecipient: boolean;
    shouldAddDedication: boolean;
    softCredit: SoftCreditFormValues['softCredit'];
    appeal_id: string[];
}

interface TransactionTeamMemberInputProps {
    campaignId: number;
}

const TransactionTeamMemberInput = (props: TransactionTeamMemberInputProps) => {
    const { data: transaction } = useCurrentPossibleTransaction();
    const { control, path } =
        Form.useFormContext<Pick<TransactionsFormValues, 'selectedTeamMember'>>();
    const { campaignId } = props;
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const { ref, data } = useInfiniteCampaignMembers(
        {
            campaignId: campaignId,
            query: debouncedSearch,
        },
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: Infinity,
        },
    );

    const members =
        data?.pages ?? (transaction?.campaign_member ? [transaction.campaign_member] : []);

    return (
        <>
            <Form.Combobox control={control} name="selectedTeamMember" allowMultiple={false}>
                <Combobox.Trigger aria-label="Select a member">
                    <Combobox.Select placeholder="Select a member" />
                </Combobox.Trigger>
                <Combobox.Popover>
                    <Combobox.Search
                        value={search}
                        onChangeValue={setSearch}
                        placeholder="Search team members"
                    />
                    <Combobox.Items>
                        {members.map(member => {
                            return (
                                <Combobox.Item key={member.id} value={member.id.toString()}>
                                    <Combobox.ItemText>
                                        <span className="text-sm font-medium">
                                            {member.display_name}
                                        </span>
                                    </Combobox.ItemText>
                                    <Combobox.CheckmarkIndicator />
                                </Combobox.Item>
                            );
                        })}
                        {members.length === 0 && (
                            <Combobox.NoneFoundText>No members found.</Combobox.NoneFoundText>
                        )}
                        <div ref={ref} />
                    </Combobox.Items>
                </Combobox.Popover>
            </Form.Combobox>
            <Form.Error name={path('selectedTeamMember')} />
        </>
    );
};

interface TransactionTeamInputProps {
    campaignId: number;
}

const TransactionTeamInput = (props: TransactionTeamInputProps) => {
    const { data: transaction } = useCurrentPossibleTransaction();
    const { control, path } = Form.useFormContext<Pick<TransactionsFormValues, 'selectedTeam'>>();
    const { campaignId } = props;
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const { ref, data } = useInfiniteCampaignTeams(
        {
            campaignId: campaignId,
            query: debouncedSearch,
        },
        {
            staleTime: Infinity,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
        },
    );

    const teams = data?.pages ?? (transaction?.campaign_team ? [transaction.campaign_team] : []);

    return (
        <>
            <Form.Combobox control={control} name="selectedTeam" allowMultiple={false}>
                <Combobox.Trigger aria-label="Select a team">
                    <Combobox.Select placeholder="Select team" />
                </Combobox.Trigger>
                <Combobox.Popover>
                    <Combobox.Search
                        value={search}
                        onChangeValue={setSearch}
                        placeholder="Search team members"
                    />
                    <Combobox.Items>
                        {teams.map(team => {
                            return (
                                <Combobox.Item key={team.id} value={team.id.toString()}>
                                    <Combobox.ItemText>
                                        <span className="text-sm font-medium">{team.name}</span>
                                    </Combobox.ItemText>
                                    <Combobox.CheckmarkIndicator />
                                </Combobox.Item>
                            );
                        })}
                        {teams.length === 0 && (
                            <Combobox.NoneFoundText>No teams found.</Combobox.NoneFoundText>
                        )}
                        <div ref={ref} />
                    </Combobox.Items>
                </Combobox.Popover>
            </Form.Combobox>
            <Form.Error name={path('selectedTeam')} />
        </>
    );
};

const TransactionFundInput = () => {
    const { accountId } = useCurrentAccount();
    const { data: transaction } = useCurrentPossibleTransaction();
    const { control, path } = Form.useFormContext<Pick<TransactionsFormValues, 'selectedFund'>>();

    const { data: fundsData } = useSuspenseQuery({
        queryKey: accountQueryKeys.fundsWithFilters(accountId, FUNDS_PARAMS),
        queryFn: () => fetchFunds(accountId, FUNDS_PARAMS),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    const funds = fundsData.data ?? (transaction?.fund ? [transaction.fund] : []);

    return (
        <>
            <Form.Combobox control={control} name="selectedFund" allowMultiple={false}>
                <Combobox.Trigger aria-label="Select a fund">
                    <Combobox.Select placeholder="Select a fund" />
                </Combobox.Trigger>
                <Combobox.Popover>
                    <Combobox.Search />
                    <Combobox.Items>
                        {funds.length === 0 && (
                            <>
                                <Combobox.NoneFoundText>No funds found</Combobox.NoneFoundText>
                                <Link
                                    className="flex items-center gap-1 p-1.5"
                                    to={`/accounts/${accountId}/settings/settings/funds/new`}
                                >
                                    <p>Create a fund</p>
                                    <FontAwesomeIcon size="sm" icon={faArrowRight} />
                                </Link>
                            </>
                        )}
                        {funds.map(fund => {
                            return (
                                <Combobox.Item key={fund.id} value={fund.id.toString()}>
                                    <Combobox.ItemText>
                                        <span className="text-sm font-medium">{fund.name}</span>
                                    </Combobox.ItemText>
                                    <Combobox.CheckmarkIndicator />
                                </Combobox.Item>
                            );
                        })}
                    </Combobox.Items>
                </Combobox.Popover>
            </Form.Combobox>
            <Form.Error name={path('selectedFund')} />
        </>
    );
};

interface TransactionDedicationInputProps {
    variant?: 'create' | 'update';
}

const TransactionDedicationInput = (props: TransactionDedicationInputProps) => {
    const { variant } = props;
    const { control, watch, path } = Form.useFormContext<TransactionsFormValues>();
    const isUpdate = variant === 'update';
    const [recipientEmail, recipientName, shouldNotify] = watch([
        'dedicationRecipientEmail',
        'dedicationRecipientName',
        'dedicationShouldNotifyRecipient',
    ]);
    const hasRecipientEmail = recipientEmail.length > 0;
    const hasRecipientName = recipientName.length > 0;

    return (
        <div className={'flex flex-col gap-4'}>
            <Form.Container>
                <Form.Label name={path('dedicationType')}>Dedication type</Form.Label>
                <Form.SingleSelect control={control} name="dedicationType">
                    <SingleSelect.Trigger aria-label="Dedication type" />
                    <SingleSelect.Content>
                        <SingleSelect.Item value="in_honor_of">
                            <SingleSelect.ItemText>In honor of</SingleSelect.ItemText>
                        </SingleSelect.Item>
                        <SingleSelect.Item value="in_memory_of">
                            <SingleSelect.ItemText>In memory of</SingleSelect.ItemText>
                        </SingleSelect.Item>
                    </SingleSelect.Content>
                </Form.SingleSelect>
            </Form.Container>
            <Form.Container>
                <Form.Label name={path('dedicationName')}>Dedication name</Form.Label>
                <Form.TextInputRoot
                    control={control}
                    name={'dedicationName'}
                    rules={{
                        required:
                            (hasRecipientEmail || hasRecipientName) &&
                            'Dedication name is required',
                    }}
                >
                    <Form.TextInputForm />
                </Form.TextInputRoot>
                <Form.Error name={path('dedicationName')} />
            </Form.Container>
            <Form.Container className={'flex-row items-center gap-2'}>
                <Form.UseFormCheckboxInput
                    control={control}
                    name={'dedicationShouldNotifyRecipient'}
                />
                <Form.Label name={path('dedicationShouldNotifyRecipient')}>
                    Send an email notification to a recipient of your choice
                </Form.Label>
            </Form.Container>
            {shouldNotify && (
                <>
                    <Form.Container>
                        <Form.Label name={path('dedicationRecipientName')}>
                            Notification recipient name
                        </Form.Label>
                        <Form.TextInputRoot
                            control={control}
                            name={'dedicationRecipientName'}
                            rules={{
                                required:
                                    hasRecipientEmail &&
                                    'Recipient name is required alongside a recipient email.',
                            }}
                        >
                            <Form.TextInputForm />
                        </Form.TextInputRoot>
                        <Form.Error name={path('dedicationRecipientName')} />
                    </Form.Container>
                    <Form.Container>
                        <Form.Label name={path('dedicationRecipientEmail')}>
                            Notification recipient email
                        </Form.Label>
                        <Form.TextInputRoot
                            control={control}
                            name={'dedicationRecipientEmail'}
                            rules={{
                                required:
                                    hasRecipientName &&
                                    'Recipient email is required alongside a recipient name.',
                            }}
                        >
                            <Form.TextInputForm />
                        </Form.TextInputRoot>
                        {isUpdate && (
                            <p className={'text-xs text-gray-600'}>
                                An email won't be triggered if it was previously sent to the email
                                address provided.
                            </p>
                        )}
                        <Form.Error name={path('dedicationRecipientEmail')} />
                    </Form.Container>
                </>
            )}
        </div>
    );
};

const TeamInput = TransactionTeamInput;
const MemberInput = TransactionTeamMemberInput;
const FundInput = TransactionFundInput;
const Dedication = TransactionDedicationInput;

export {
    TransactionTeamInput,
    TransactionTeamMemberInput,
    TransactionFundInput,
    TransactionDedicationInput,
    //
    TeamInput as Team,
    MemberInput as Member,
    FundInput as Fund,
    Dedication,
};
export type { TransactionsFormValues };
